import React from "react";

import { useLocales } from "hooks";
import { MapKind, Badge } from "components";
import { displayDateTime } from "utils";

interface BaseItem {
  region: string;
  canton: string;
  valid_from: string;
  valid_until: string | null;
}

type WarnMapItem = BaseItem & {
  level: number;
};

type MeasureItem = BaseItem & {
  category: number;
};

export type HistoryItem = MeasureItem | WarnMapItem;

export interface FireHistoryTableProps {
  type: MapKind;
  loadMore: boolean;
  onLoadMore: () => void;
  data: Array<HistoryItem> | null;
  loading: boolean;
}

export function FireHistoryTable({
  type,
  loadMore,
  onLoadMore,
  data,
  loading,
}: FireHistoryTableProps): JSX.Element {
  const { t } = useLocales();

  const levelOrCategory = (entry: HistoryItem) => {
    return type === "warnMap"
      ? (entry as WarnMapItem).level
      : (entry as MeasureItem).category;
  };

  return (
    <React.Fragment>
      <div className="table-responsive">
        <table className="table table-bordered fire-history-table">
          <thead>
            <tr>
              <th>{t("history.table.region")}</th>
              <th>{t("history.table.canton")}</th>
              <th>
                {t(
                  `history.table.${type === "warnMap" ? "level" : "category"}`
                )}
              </th>
              <th>{t("history.table.validFrom")}</th>
              <th>{t("history.table.validUntil")}</th>
            </tr>
          </thead>
          <tbody>
            {data?.map((entry, idx) => (
              <tr key={idx}>
                <td>{entry.region}</td>
                <td>{entry.canton}</td>
                <td>
                  <div className="flex align-center">
                    <Badge type={type} level={levelOrCategory(entry)} />
                    {t(`legend.${type}.${levelOrCategory(entry)}`)}
                  </div>
                </td>
                <td>{displayDateTime(entry.valid_from)}</td>
                <td>
                  {!!entry.valid_until && displayDateTime(entry.valid_until)}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
      <div className="flex align-center justify-center">
        {data?.length === 0 && !loading && <p>{t("history.table.empty")}</p>}
        {loadMore && (
          <button
            disabled={loading}
            aria-label={t("loadMore")}
            name={t("loadMore")}
            onClick={onLoadMore}
          >
            {loading ? t("loading") : t("loadMore")}
          </button>
        )}
      </div>
    </React.Fragment>
  );
}
